import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * VirtualHList: simple horizontal windowing for flex-based carousels.
 * - Keeps DOM light by rendering only visible items + overscan.
 * - Preserves existing styles by keeping the same container class.
 * - Works with external scroll buttons via provided containerRef.
 */
const VirtualHList = ({
  items = [],
  renderItem,
  overscan = 4,
  containerClassName = 'anime-line-container',
  containerRef: externalRef,
}) => {
  const internalRef = useRef(null);
  const ref = externalRef || internalRef;

  // Use fixed sizes based on existing CSS to avoid measuring
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 720;
  const itemWidth = isMobile ? 140 : 200; // from AnimeCardComponent.css
  const itemMargin = isMobile ? 5 : 10;   // horizontal margins
  const itemFull = itemWidth + itemMargin * 2;

  const [range, setRange] = useState({ start: 0, end: Math.min(items.length, 20) });

  const updateRange = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const { scrollLeft, clientWidth } = el;
    const perScreen = Math.max(1, Math.ceil(clientWidth / itemFull));
    const start = Math.max(0, Math.floor(scrollLeft / itemFull) - overscan);
    const end = Math.min(items.length, start + perScreen + overscan * 2);

    setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, [items.length, itemFull, overscan, ref]);

  useEffect(() => {
    // Initial calculation and listeners
    updateRange();
    const el = ref.current;
    if (!el) return;

    const onScroll = () => updateRange();
    el.addEventListener('scroll', onScroll, { passive: true });
    const onResize = () => updateRange();
    window.addEventListener('resize', onResize);

    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [updateRange, ref]);

  // For short lists, render all to keep logic simple
  const useVirtual = items.length > 24; // threshold to enable virtualization

  const visibleItems = useMemo(() => {
    if (!useVirtual) return items;
    return items.slice(range.start, range.end);
  }, [items, range, useVirtual]);

  const preSpacerWidth = useVirtual ? range.start * itemFull : 0;
  const postSpacerWidth = useVirtual ? (items.length - range.end) * itemFull : 0;

  return (
    <div className={containerClassName} ref={ref}>
      {useVirtual && preSpacerWidth > 0 ? (
        <div style={{ width: preSpacerWidth, flex: '0 0 auto' }} />
      ) : null}

      {visibleItems.map((item, i) => {
        const actualIndex = useVirtual ? range.start + i : i;
        return (
          <React.Fragment key={item.id ?? actualIndex}>
            {renderItem(item, actualIndex)}
          </React.Fragment>
        );
      })}

      {useVirtual && postSpacerWidth > 0 ? (
        <div style={{ width: postSpacerWidth, flex: '0 0 auto' }} />
      ) : null}
    </div>
  );
};

export default VirtualHList;

